// Which registry country a drawn shape is in, worked out from where it lies. Pure: no React,
// router, Firebase or services imports. The boxes are coarse (see CountryDef.extent), so a shape
// near a border can be ambiguous; then the answer is null and the person picks.
import { COUNTRY_LIST } from '../config/countries';
import type { BBox, CountryCode } from '../config/countries';
import { shapeToRing } from './polygonEditor';

type Point = readonly [number, number] | readonly number[];

const inBox = (b: BBox, x: number, y: number) => x >= b[0] && x <= b[2] && y >= b[1] && y <= b[3];
const inExtent = (extent: readonly BBox[], p: Point) => extent.some((b) => inBox(b, p[0], p[1]));
const boxArea = (b: BBox) => (b[2] - b[0]) * (b[3] - b[1]);

/** Area centroid of a ring (shoelace); the mean of the points when the ring has no area. */
function centroid(ring: readonly Point[]): [number, number] {
  let a = 0;
  let cx = 0;
  let cy = 0;
  for (let i = 0; i < ring.length; i++) {
    const [x0, y0] = ring[i];
    const [x1, y1] = ring[(i + 1) % ring.length];
    const cross = x0 * y1 - x1 * y0;
    a += cross;
    cx += (x0 + x1) * cross;
    cy += (y0 + y1) * cross;
  }
  if (Math.abs(a) < 1e-12) {
    const n = ring.length;
    return [ring.reduce((s, p) => s + p[0], 0) / n, ring.reduce((s, p) => s + p[1], 0) / n];
  }
  return [cx / (3 * a), cy / (3 * a)];
}

/**
 * The country a ring ([longitude, latitude] points) or a POLYGON WKT lies in, or null.
 * The centroid picks the country. No country at the centroid gives null. When the centroid sits
 * in the boxes of more than one country, the one with the smallest extent that holds every point
 * wins; if none holds them all the answer is null.
 */
export function countryForGeometry(geometry: readonly Point[] | string): CountryCode | null {
  const ring = typeof geometry === 'string' ? shapeToRing(geometry)?.ring : geometry;
  if (!ring || ring.length === 0 || ring.some((p) => !Number.isFinite(p[0]) || !Number.isFinite(p[1]))) return null;

  const [cx, cy] = centroid(ring);
  const hits = COUNTRY_LIST.filter((c) => inExtent(c.extent, [cx, cy]));
  if (hits.length === 0) return null;
  if (hits.length === 1) return hits[0].code;

  const holdingAll = hits.filter((c) => ring.every((p) => inExtent(c.extent, p)));
  if (holdingAll.length === 0) return null;
  const size = (extent: readonly BBox[]) => extent.reduce((s, b) => s + boxArea(b), 0);
  holdingAll.sort((a, b) => size(a.extent) - size(b.extent));
  return holdingAll[0].code;
}
